import React from "react";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { Modal } from "react-bootstrap";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { SET_NUM_UNREAD_ALERTS } from "@/modules/pg/shared/planningTable/reducers/planningTable/alertsReducer";
import ReactTable from "@/modules/shared/ui/ReactTable";
import { niceDateTime } from "@/lib/util";

import type { Row } from "react-table";
import type {
    SetNumUnreadAlerts,
    AlertsActions,
} from "@/modules/pg/shared/planningTable/reducers/planningTable/alertsReducer";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { ReactTableColumn } from "@/modules/shared/ui/ReactTable";
import type { FilteredTypeOrder } from "@/lib/ganttData";

import "./AlertsModal.scss";

type AlertsModalActionTypes = AlertsActions;

type AlertsModalOwnProps = {
    show_alerts_modal: boolean;
    onCloseAlertsModal: () => void;
};

type Props = AlertsModalOwnProps & AlertsModalReduxProps;

type Column = FilteredTypeOrder & {
    created_at: string;
    original_created_at: number;
    message: string;
    unread: boolean;
    line_title: string;
};

const rowStyle = (row: Row<Column>): React.CSSProperties => ({
    backgroundColor: row.values.unread ? "#eaeaea" : "unset",
});

const getColumns = (): ReactTableColumn<Partial<Column>>[] => {
    return [
        {
            Header: "",
            filterInputPlaceholder: translate("common.line", "Line"),
            accessor: "line_title",
        },
        {
            Header: "",
            filterInputPlaceholder: translate("common.order_external_id", "Order external ID"),
            accessor: "order_external_id",
        },
        {
            Header: "",
            filterInputPlaceholder: translate("common.process_num", "Process Num"),
            accessor: "process_num",
        },
        {
            Header: "",
            filterInputPlaceholder: translate("common.messages", "Message"),
            accessor: "message",
        },
        {
            Header: translate("OrderTableProduction.created_at", "Created At"),
            accessor: "created_at",
            disableFilters: true,
        },
        {
            Header: "Read",
            accessor: "unread",
            disableFilters: true,
        },
    ];
};

class AlertsModal extends React.Component<Props> {
    getData(): Column[] {
        const alerts: Column[] = [];
        for (const alert of this.props.alerts) {
            for (const line of alert.filtered_lines || []) {
                for (const order of line.orders) {
                    alerts.push({
                        message: order.message || "",
                        created_at: niceDateTime(new Date(alert.created_at)),
                        original_created_at: alert.created_at,
                        line_title: line.line_title ? line.line_title : "-",
                        process_num: order.process_num,
                        order_external_id: order.order_external_id,
                        unread: alert.unread,
                    });
                }
            }
            for (const past_order of alert.past_orders || []) {
                alerts.push({
                    message:
                        translate("common.order_is_in_the_past", "Order is in the past") +
                        " - " +
                        niceDateTime(new Date(past_order.earliest_start)),
                    created_at: niceDateTime(new Date(alert.created_at)),
                    original_created_at: alert.created_at,
                    line_title: past_order.line_title ? past_order.line_title : "-",
                    process_num: past_order.process_num,
                    order_external_id: past_order.external_id,
                    unread: alert.unread,
                });
            }
        }
        return alerts.sort((a, b) => (a.original_created_at > b.original_created_at ? -1 : 1));
    }

    render() {
        const data = this.getData();
        const columns = getColumns();

        return (
            <Modal
                show={this.props.show_alerts_modal}
                id="alerts-modal"
                onHide={this.props.onCloseAlertsModal}
                size="lg"
            >
                <Modal.Header>
                    <Modal.Title style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>{translate("Header.menu.alerts", "Alerts")}</span>
                            <button
                                type="button"
                                className="close"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    this.props.onCloseAlertsModal();
                                }}
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ReactTable columns={columns} hiddenColumns={["unread"]} rowStyle={rowStyle} data={data} />
                </Modal.Body>
            </Modal>
        );
    }
}

const alertsModalMapState = (state: ReduxState) => {
    const alerts = state.alerts.alerts;
    return { alerts };
};

const alertsModalMapDispatch = (dispatch: Dispatch<AlertsModalActionTypes>) => ({
    reduxDispatch: dispatch,
});

type AlertsModalReduxProps = ReturnType<typeof alertsModalMapState> & ReturnType<typeof alertsModalMapDispatch>;

const ConnectedAlertsModal = connect<
    ReturnType<typeof alertsModalMapState>,
    ReturnType<typeof alertsModalMapDispatch>,
    AlertsModalOwnProps,
    ReduxState
>(
    alertsModalMapState,
    alertsModalMapDispatch
)(AlertsModal);

type ViewMenuAlertsActionTypes = SetNumUnreadAlerts;

type ViewMenuAlertsOwnProps = {};
type ViewMenuAlertsProps = ViewMenuAlertsOwnProps & ViewMenuAlertsReduxProps;

type ViewMenuAlertsState = {
    show_alerts_modal: boolean;
};

export const DARK_RED = "#bb1e1d";

class ViewMenuAlerts extends React.Component<ViewMenuAlertsProps, ViewMenuAlertsState> {
    state: ViewMenuAlertsState = {
        show_alerts_modal: false,
    };

    onShowAlertsModal = () => {
        this.setState({ show_alerts_modal: true });
    };

    onCloseAlertsModal = () => {
        this.props.reduxDispatch({ type: SET_NUM_UNREAD_ALERTS, data: 0 });
        this.setState({ show_alerts_modal: false });
    };

    render() {
        return (
            <button className="dropdown-item" onClick={this.onShowAlertsModal}>
                {translate("Header.menu.alerts", "Alerts")}{" "}
                {this.props.num_unread_alerts ? `(${this.props.num_unread_alerts})` : ""}
                <ConnectedAlertsModal
                    show_alerts_modal={this.state.show_alerts_modal}
                    onCloseAlertsModal={this.onCloseAlertsModal}
                />
            </button>
        );
    }
}

const viewMenuAlertsMapState = (state: ReduxState) => ({
    num_unread_alerts: state.alerts.num_unread_alerts,
});

const viewMenuAlertsMapDispatch = (dispatch: Dispatch<ViewMenuAlertsActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ViewMenuAlertsReduxProps = ReturnType<typeof viewMenuAlertsMapState> &
    ReturnType<typeof viewMenuAlertsMapDispatch>;

export const ConnectedViewMenuAlerts = connect<
    ReturnType<typeof viewMenuAlertsMapState>,
    ReturnType<typeof viewMenuAlertsMapDispatch>,
    ViewMenuAlertsOwnProps,
    ReduxState
>(
    viewMenuAlertsMapState,
    viewMenuAlertsMapDispatch
)(ViewMenuAlerts);
